import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import { usePermissions } from '@/hooks/usePermissions';
import type { FinDocumentCapture } from '@/types/finance';
import { showApiError } from '@/utils/apiError';

export function FinanceDocumentCapturePage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('FINANCE_AP_MANAGE', 'FINANCE_MANAGE');
  const [documents, setDocuments] = useState<FinDocumentCapture[]>([]);
  const [fileName, setFileName] = useState('');
  const [rawText, setRawText] = useState('');
  const [documentType, setDocumentType] = useState('VENDOR_BILL');
  const [submitting, setSubmitting] = useState(false);
  const { rows: paginatedDocuments, tablePaginationProps: documentsPaginationProps } =
    useClientPaginatedRows(documents);

  const load = () => financeApi.listDocumentCaptures().then(setDocuments).catch((e) => showApiError(e, 'Failed to load documents'));

  useEffect(() => {
    load();
  }, []);

  const capture = () => {
    if (!fileName.trim()) return;
    setSubmitting(true);
    financeApi
      .captureDocument({ documentType, fileName: fileName.trim(), rawText: rawText.trim() || undefined })
      .then(() => {
        setFileName('');
        setRawText('');
        load();
      })
      .catch((e) => showApiError(e, 'Capture failed'))
      .finally(() => setSubmitting(false));
  };

  const approve = (id: number) => {
    financeApi.approveDocumentCapture(id, 'Reviewed and approved').then(load).catch((e) => showApiError(e, 'Approve failed'));
  };

  const reject = (id: number) => {
    financeApi.rejectDocumentCapture(id, 'Rejected during review').then(load).catch((e) => showApiError(e, 'Reject failed'));
  };

  return (
    <div>
      <PageHeader
        title="Document capture (OCR)"
        description="Upload vendor bills and receipts — extract fields, review, then post"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Payables', to: '/finance/payables' },
          { label: 'Document capture' },
        ]}
      />

      <div className="card p-4 mb-6 space-y-3">
        <h3 className="font-semibold">Capture document</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-sm">
            Document type
            <select className="input mt-1 w-full" value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
              <option value="VENDOR_BILL">Vendor bill</option>
              <option value="RECEIPT">Receipt</option>
              <option value="TAX_DOCUMENT">Tax document</option>
              <option value="BANK_STATEMENT">Bank statement</option>
              <option value="EXPENSE_RECEIPT">Expense receipt</option>
            </select>
          </label>
          <label className="text-sm">
            File name
            <input className="input mt-1 w-full" value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="invoice-acme-1042.pdf" />
          </label>
        </div>
        <label className="text-sm block">
          Paste text for extraction (optional)
          <textarea
            className="input mt-1 w-full min-h-[100px]"
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="Acme Supplies&#10;Invoice INV-1042&#10;Total: 1,250.00&#10;Date: 2026-05-15"
          />
        </label>
        {canManage && (
          <Button variant="primary" onClick={capture} disabled={submitting || !fileName.trim()}>
            {submitting ? 'Extracting…' : 'Extract & queue for review'}
          </Button>
        )}
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-3">File</th>
              <th className="p-3">Vendor</th>
              <th className="p-3">Invoice #</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {documents.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-slate-500">
                  No captured documents. Paste bill text above to extract vendor, amount, and date automatically.
                </td>
              </tr>
            ) : (
              paginatedDocuments.map((doc) => (
                <tr key={doc.id} className="border-t">
                  <td className="p-3">{doc.fileName}</td>
                  <td className="p-3">{doc.vendorName ?? '—'}</td>
                  <td className="p-3">{doc.invoiceNumber ?? '—'}</td>
                  <td className="p-3 tabular-nums">{doc.amount?.toFixed(2) ?? '—'}</td>
                  <td className="p-3">{doc.statusCode}</td>
                  <td className="p-3 space-x-2">
                    {doc.statusCode === 'PENDING_REVIEW' && canManage && (
                      <>
                        <button type="button" className="text-primary-600 text-xs hover:underline" onClick={() => approve(doc.id)}>
                          Approve
                        </button>
                        <button type="button" className="text-red-600 text-xs hover:underline" onClick={() => reject(doc.id)}>
                          Reject
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <TablePagination {...documentsPaginationProps} hasRows={documents.length > 0} />
      </div>
    </div>
  );
}
